import React, { FC } from 'react';
import styled from '@emotion/styled';
import { StorageData, useStorage } from '~Components/Storage';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import Toggle from '~ComponentsImproved/Toggle';
import { useI18n, useLogger } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import OrderMock from './OrderMock';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import MasonryIcon from '~Assets/icons/masonry.svg';
import GridIcon from '~Assets/icons/grid.svg';
import IconSelector from '~Components/IconSelector';
import { useDeviceHandler } from '~Components/DeviceProvider';

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 16px;
`;

const MasonryWrapper = styled.div`
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
`;

const MasonryBox = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 16px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'auto')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const getSelectedValue = (
  key: keyof SelectedValues,
  styleMultiplier: {
    size: number;
    space: number;
  },
): string => {
  if (key === 'size') {
    return styleMultiplier.size === 3
      ? 'size-smallest'
      : styleMultiplier.size === 2
      ? 'size-smaller'
      : styleMultiplier.size === 1
      ? 'size-small'
      : styleMultiplier.size === 0
      ? 'size-default'
      : styleMultiplier.size === -1
      ? 'size-big'
      : 'size-bigger';
  } else if (key === 'space') {
    return styleMultiplier.space === 3
      ? 'space-smallest'
      : styleMultiplier.space === 2
      ? 'space-smaller'
      : styleMultiplier.space === 1
      ? 'space-small'
      : 'space-default';
  }
  return 'space-default';
};

interface SelectedValues {
  size: string;
  space: string;
}

const SettingsWrapper = styled.div`
  flex: 1;
`;

const KDSStylingSettings: FC = () => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');
  const deviceHandler = useDeviceHandler();
  const isShowMasonryGridRemotelyControlled =
    deviceHandler.remotelyControlledData['showMasonryGrid'];
  const isMasonryDisabled = Boolean(isShowMasonryGridRemotelyControlled);

  const handleRadioButtonChange = (key: string, value: number, id: string) => {
    logger.info('Updating Settings (KDS Layout)', { fields: { key, value } });
    commitToStorage({
      styleMultiplier: {
        ...storage.styleMultiplier,
        [key]: value,
      },
    });
  };

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (KDS Layout)', { fields: data });
    commitToStorage(data);
  }

  const minimalToggle = {
    label: i18n('Settings.KDS.Lite'),
    subLabel: i18n('Settings.KDS.Lite.Text'),
    key: 'KDSLite',
    storage: storage.KDSLite,
  };

  const togglesConfig: Array<{
    label: string;
    subLabel: string;
    key: string;
    storage: any;
  }> = [
    {
      label: i18n('Settings.KDS.ShowBottomBar'),
      subLabel: i18n('Settings.KDS.ShowBottomBar.Text'),
      key: 'showOrderBottomBar',
      storage: storage.showOrderBottomBar,
    },
    {
      label: i18n('Settings.KDS.ShowETA'),
      subLabel: i18n('Settings.KDS.ShowETA.Text'),
      key: 'showOrderEtaBar',
      storage: storage.showOrderEtaBar,
    },
    {
      label: i18n('Settings.KDS.FiveColumns'),
      subLabel: i18n('Settings.KDS.FiveColumns.Text'),
      key: 'showFiveColumns',
      storage: storage.showFiveColumns,
    },
    {
      label: i18n('Settings.KDS.MarkAddedVariationsGreen'),
      subLabel: i18n('Settings.KDS.MarkAddedVariationsGreen.Text'),
      key: 'markAddedVariationsGreen',
      storage: storage.markAddedVariationsGreen,
    },
  ];

  const radioButtonsConfig: {
    containerLabel: string;
    name: keyof SelectedValues;
    buttons: Array<{
      id: string;
      label: string;
      value: number;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.KDS.FontSize'),
      name: 'size',
      buttons: [
        {
          id: 'size-smallest',
          label: i18n('Settings.KDS.Smallest'),
          value: 3,
        },
        {
          id: 'size-smaller',
          label: i18n('Settings.KDS.Smaller'),
          value: 2,
        },
        {
          id: 'size-small',
          label: i18n('Settings.KDS.Small'),
          value: 1,
        },
        {
          id: 'size-default',
          label: i18n('Settings.KDS.Default'),
          value: 0,
        },
        {
          id: 'size-big',
          label: i18n('Settings.KDS.Big'),
          value: -1,
        },
        {
          id: 'size-bigger',
          label: i18n('Settings.KDS.Bigger'),
          value: -2,
        },
      ],
    },
    {
      containerLabel: i18n('Settings.KDS.MarginSize'),
      name: 'space',
      buttons: [
        {
          id: 'space-smallest',
          label: i18n('Settings.KDS.Smallest'),
          value: 3,
        },
        {
          id: 'space-smaller',
          label: i18n('Settings.KDS.Smaller'),
          value: 2,
        },
        {
          id: 'space-small',
          label: i18n('Settings.KDS.Small'),
          value: 1,
        },
        {
          id: 'space-default',
          label: i18n('Settings.KDS.Default'),
          value: 0,
        },
      ],
    },
  ];

  if (storageLoading) {
    return null;
  }

  return (
    <Box display="flex" gap="large" marginBottom="xl">
      <SettingsWrapper>
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.Styling')}
        </Heading>
        <SettingsBlock>
          <Toggle
            label={minimalToggle.label}
            settingKey="KDSLite"
            description={minimalToggle.subLabel}
            initialValue={minimalToggle.storage}
            onToggle={() => updateStorage({ KDSLite: !storage.KDSLite })}
            fullWidth
            darkBackground
            lightText
          />
          {!storage.KDSLite && (
            <MasonryWrapper>
              <MasonryBox disabled={isMasonryDisabled}>
                <Text>
                  <Heading level={5} color="var(--colorWhite)">
                    {i18n('Settings.KDS.ChooseLayout')}
                  </Heading>
                  <Paragraph level={5} color="var(--colorBlueGray300)">
                    {i18n('Settings.KDS.ChooseLayout.Text')}
                  </Paragraph>
                </Text>
                <Box display="flex" flexDirection="column" alignItems="flex-end" gap="xs">
                  <ButtonsWrapper>
                    <IconSelector
                      title={i18n('Settings.KDS.ChooseLayout.Compact')}
                      iconSrc={MasonryIcon}
                      selected={storage.showMasonryGrid}
                      onClick={() => updateStorage({ showMasonryGrid: true })}
                    />
                    <IconSelector
                      title={i18n('Settings.KDS.ChooseLayout.Grid')}
                      iconSrc={GridIcon}
                      selected={!storage.showMasonryGrid}
                      onClick={() => updateStorage({ showMasonryGrid: false })}
                    />
                  </ButtonsWrapper>
                  {Boolean(isShowMasonryGridRemotelyControlled) && (
                    <Paragraph level={5} color="var(--colorBlueGray300)">
                      ({i18n('Settings.LockedByBaemingo')})
                    </Paragraph>
                  )}
                </Box>
              </MasonryBox>
            </MasonryWrapper>
          )}
          {!storage.KDSLite &&
            togglesConfig.map((toggle) => (
              <Toggle
                key={toggle.key}
                settingKey={toggle.key}
                label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
                description={toggle.subLabel}
                initialValue={toggle.storage}
                onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
                fullWidth
                darkBackground
                lightText
              />
            ))}
          {radioButtonsConfig.map((config) => (
            <RadioButtonSelect
              key={config.name}
              settingKey="styleMultiplier"
              data={config}
              selected={getSelectedValue(config.name, storage.styleMultiplier)}
              handleChange={(value, id) => handleRadioButtonChange(config.name, value, id)}
            />
          ))}
        </SettingsBlock>
      </SettingsWrapper>
      <OrderMock />
    </Box>
  );
};

export default KDSStylingSettings;
